import type { EnabledAddonsType } from '@/hooks/useListEnabledAddons';
import type { MetaAddonsType } from '@/hooks/useAddonsMeta';
import { FC, useMemo } from 'react';
import { Drawer, DrawerAction, DrawerBody, DrawerHeader } from '@/components/Drawer';

import SolutionIcon from '@/assets/solution.svg?react';
import ConfirmButtons from '@/components/ConfirmButtons';
import EmblaCarousel, { EmblaCarouselItem } from '@/pages/marketplace/solution/carousel/EmblaCarousel';
import AddonsDrawerCommonHeader from '@/components/addons/AddonsCommonHeader';
import { FormProvider, useForm } from 'react-hook-form';
import { AddonsMetaID } from '@/components/addons/consts';
import WorkspaceAddonsEnableCopilotDrawer from '@/pages/workgroup/form/addons/drawer/copilot';
import { createWorkspaceEnableAtom, useAddonsIsEnabled } from '@/pages/workgroup/form/addons/store';
import { useSetAtom } from 'jotai';
import { getAddonPrice } from '@/pages/workgroup/type';
import { useWorkspaceContext } from '@/contexts/workspaceContext';

const drawerConfigMap = new Map<string, FC<{ enabledAddons: EnabledAddonsType; addonsMeta: MetaAddonsType }>>([
  [AddonsMetaID.COPILOT, WorkspaceAddonsEnableCopilotDrawer],
]);

const WorkspaceAddonsEnableDrawer: FC<{
  enabledAddons?: EnabledAddonsType;
  addonsMeta?: MetaAddonsType;
  computePrice?: number;
  onCancel?(): void;
  onChange?(params: Record<string, string | number | boolean>): void;
  workspaceType: string;
  platform: string;
  region: string;
}> = ({ enabledAddons, addonsMeta, computePrice, onCancel, onChange, workspaceType, platform, region }) => {
  const descriptionList = addonsMeta?.Announcement || [addonsMeta?.Description];
  const setAddonsEnable = useSetAtom(createWorkspaceEnableAtom);
  const isEnabled = useAddonsIsEnabled(enabledAddons?.AddonsID as string);
  const { price } = useWorkspaceContext();
  const form = useForm({
    defaultValues: {
      copilot_llm_provider_config_id: '',
    },
  });
  const { handleSubmit } = form;
  const DrawerFormContent = drawerConfigMap.get(enabledAddons?.AddonsID as string);
  const amount = useMemo(() => {
    if (!computePrice || !price) {
      return '--';
    }
    if (addonsMeta?.ID === AddonsMetaID.COPILOT) {
      return `${getAddonPrice(price, 'CoPilot', workspaceType, platform, region)} TCR`;
    } else if (addonsMeta?.ID === AddonsMetaID.INSIGHTS) {
      return `${getAddonPrice(price, 'Insights', workspaceType, platform, region)} TCR`;
    } else {
      return 'Free';
    }
  }, [computePrice, addonsMeta?.ID, price, workspaceType, platform, region]);

  const handleConfirm = handleSubmit((value) => {
    setAddonsEnable((v) => ({ ...v, [enabledAddons?.AddonsID as string]: !isEnabled }));
    if (isEnabled) {
      // for disable confirm, clean all data
      form.reset();
      onChange?.(form.getValues());
    } else {
      // for enable confirm
      onChange?.(value);
    }
  });

  const handleClose = () => {
    if (isEnabled) {
      // update form data if enabled
      handleSubmit((value) => onChange?.(value))();
    } else {
      // cancel form data if disabled
      onCancel?.();
    }
  };

  return (
    <Drawer isOpen={Boolean(enabledAddons && addonsMeta)} onClose={handleClose}>
      <DrawerHeader>
        <SolutionIcon />
        Add-Ons
      </DrawerHeader>
      <DrawerBody>
        <div className="space-y-4">
          {addonsMeta && <AddonsDrawerCommonHeader {...addonsMeta} Amount={amount} />}
          {addonsMeta?.BannerImageList?.length && (
            <EmblaCarousel>
              {addonsMeta?.BannerImageList.map((image, i) => (
                <EmblaCarouselItem key={i}>
                  <div className="flex justify-center items-center">
                    <img className="h-[200px]" src={image} />
                  </div>
                </EmblaCarouselItem>
              ))}
            </EmblaCarousel>
          )}
          {descriptionList.map((d, idx) => (
            <p className="text-sm" key={idx}>
              {d}
            </p>
          ))}
          <FormProvider {...form}>
            {DrawerFormContent && enabledAddons && addonsMeta && (
              <DrawerFormContent enabledAddons={enabledAddons} addonsMeta={addonsMeta} />
            )}
          </FormProvider>
        </div>
      </DrawerBody>
      <DrawerAction>
        <ConfirmButtons
          confirmLabel={isEnabled ? 'Disable' : 'Enable'}
          cancelLabel="Back"
          onConfirm={handleConfirm}
          onCancel={handleClose}
        />
      </DrawerAction>
    </Drawer>
  );
};

export default WorkspaceAddonsEnableDrawer;
